import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';
import SEOHead from '../../components/SEOHead';
import { FaCompass, FaLightbulb, FaRocket, FaBrain } from 'react-icons/fa';
import { GiRadarSweep } from 'react-icons/gi';
import veloxIcon from '../../assets/Velox-icon-color.png';
import veloxIconBW from '../../assets/Velox-icon-b&w.png';
import './Homepage.css';

export default function Homepage() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isAgentVHovered, setIsAgentVHovered] = useState(false);
  const containerRef = useRef(null);
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleGetStartedClick = () => {
    // Analytics tracking will be handled by GTM
  };

  const handleLearnMoreClick = () => {
    // Analytics tracking will be handled by GTM
  };

  const handleTrialClick = () => {
    // Analytics tracking will be handled by GTM
  };

  const handleDemoClick = () => {
    // Analytics tracking will be handled by GTM
  };

  const handleModuleClick = (moduleName) => {
    // Analytics tracking will be handled by GTM
  };

  return (
    <PublicLayout>
      <SEOHead 
        title="Velox AI - Ambient Revenue Intelligence Platform"
        description="Transform deal chaos into clarity with RADAR, COMPASS, WIZARD, and Agent V. AI-powered revenue operations intelligence that moves at the speed of your buyers."
        url="/"
      />
      <div className="artistic-homepage" ref={containerRef}>
        {/* Animated Background Elements */}
        <div className="floating-shapes">
          <div className="shape shape-1" style={{
            left: `${mousePosition.x * 0.1}px`,
            top: `${mousePosition.y * 0.1}px`
          }}></div>
          <div className="shape shape-2" style={{
            left: `${mousePosition.x * -0.05}px`,
            top: `${mousePosition.y * -0.05}px`
          }}></div>
          <div className="shape shape-3" style={{
            left: `${mousePosition.x * 0.08}px`,
            top: `${mousePosition.y * 0.08}px`
          }}></div>
        </div>

        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-background">
            <div className="gradient-mesh"></div>
            <div className="noise-overlay"></div>
          </div>
          
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title">
                <span className="title-line">Ambient Revenue</span>
                <span className="title-line">Intelligence</span>
                <span className="title-line">for B2B Teams</span>
              </h1>
              
              <p className="hero-subtitle">
              Velox AI delivers intelligence where your sales teams work — guiding deals, surfacing risks, and accelerating outcomes without extra noise. Always-on, modular, and built to make intelligence embedded in your workflow.
              </p>
              
              <div className="hero-cta">
                <Link to="/waitlist" className="btn btn-primary" onClick={handleGetStartedClick}>
                  <span className="btn-text">Create Account</span>
                  <div className="btn-glow"></div>
                </Link>
                <Link to="/features" className="btn btn-secondary" onClick={handleLearnMoreClick}>
                  <span className="btn-text">Learn More</span>
                  <div className="btn-ripple"></div>
                </Link>
              </div>
            </div>

            <div className="hero-visual">
              <div className="artistic-dashboard">
                <div className="dashboard-grid">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className="grid-cell"
                      style={{
                        animationDelay: `${1 + i * 0.1}s`
                      }}
                    />
                  ))}
                </div>
                <div className="floating-elements">
                  <div className="element element-1"></div>
                  <div className="element element-2"></div>
                  <div className="element element-3"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Modules Section */}
        <section className="section modules-section">
          <div className="section-background">
            <div className="geometric-pattern"></div>
          </div>
          
          <div className="container">
            <h2 className="section-title">
              Supercharge Your Sales Process
            </h2>
            
            <p className="section-subtitle">
              Our suite of AI-powered modules works seamlessly together to eliminate blind spots, accelerate deal cycles, and help revenue teams win with confidence.
            </p>

            <div className="modules-grid">
              {/* RADAR Module */}
              <div className="module-card">
                <div className="module-icon-container">
                  <div className="module-icon">
                    <GiRadarSweep />
                  </div>
                  <div className="icon-aura"></div>
                </div>
                <h3>RADAR</h3>
                <p>
                Continuous deal monitoring powered by transcript analysis and signal detection — highlighting risks and opportunities before they escalate.
                </p>
                <Link to="/features#compass" className="module-link" onClick={() => handleModuleClick('compass')}>
                  <span>Discover More</span>
                  <div className="link-arrow">→</div>
                </Link>
              </div>
              
              {/* COMPASS Module */}
              <div className="module-card">
                <div className="module-icon-container">
                  <div className="module-icon">
                    <FaCompass />
                  </div>
                  <div className="icon-aura"></div>
                </div>
                <h3>COMPASS</h3>
                <p>
                Real-time sales assistant that supports reps with account briefs, objection handling, competitive insights, and proposal creation.
                </p>
                <Link to="/features#compass" className="module-link" onClick={() => handleModuleClick('compass')}>
                  <span>Discover More</span>
                  <div className="link-arrow">→</div>
                </Link>
              </div>

              {/* Wizard Module */}
              <div className="module-card">
                <div className="module-icon-container">
                  <div className="module-icon">
                    <FaLightbulb />
                  </div>
                  <div className="icon-aura"></div>
                </div>
                <h3>WIZARD</h3>
                <p>
                Guided workflows and deal diagnostics that surface the next best action and keep opportunities moving with precision.
                </p>
                <Link to="/features#wizard" className="module-link" onClick={() => handleModuleClick('wizard')}>
                  <span>Discover More</span>
                  <div className="link-arrow">→</div>
                </Link>
              </div>

              {/* Agent V Module */}
              <div 
                className="module-card"
                onMouseEnter={() => setIsAgentVHovered(true)}
                onMouseLeave={() => setIsAgentVHovered(false)}
              >
                <div className="module-icon-container">
                  <div className="module-icon" style={{ width: '80px', height: '80px', padding: '20px', borderRadius: '10px' }}>
                    <img 
                      src={isAgentVHovered ? veloxIconBW : veloxIcon} 
                      alt="Agent V" 
                      className="module-icon-image" 
                    />
                  </div>
                  <div className="icon-aura"></div>
                </div>
                <h3>Agent V</h3>
                <p>
                The ambient intelligence layer that orchestrates all Velox modules — proactively delivering insights, nudges, and recommendations across tools your team uses.
                </p>
                <Link to="/features#wizard" className="module-link" onClick={() => handleModuleClick('wizard')}>
                  <span>Discover More</span>
                  <div className="link-arrow">→</div>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Audience Section */}
        <section className="section audience-section">
          <div className="section-background">
            <div className="organic-shapes"></div>
          </div>
          
          <div className="container">
            <h2 className="section-title">
              For Modern Sales Organizations
            </h2>
            
            <p className="section-subtitle">
            Velox AI is designed for B2B SaaS companies who want to scale efficiently without massive overhead
            </p>

            <div className="audience-cards">
              {[
                { title: "CROs & Founders", desc: "Scale with discipline — achieve growth without ballooning headcount or drowning in fragmented tools." },
                { title: "Revenue Operations", desc: "Automated capture and analysis of buyer signals and insights without burdening reps with manual updates." },
                { title: "Account Executives", desc: "Always-on visibility into deal health, contextual intelligence, and automated workflows that let you close faster." }
              ].map((card, index) => (
                <div key={index} className="audience-card">
                  <div className="card-accent"></div>
                  <h3>{card.title}</h3>
                  <p>{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section cta-section">
          <div className="cta-background">
            <div className="cta-gradient"></div>
            <div className="cta-particles"></div>
          </div>
          
          <div className="container">
            <h2>
              Ready to Bring Ambient Intelligence to Your Sales?
            </h2>
            
            <p>
              Equip your revenue team with always-on insights, seamless workflows, and deal intelligence that moves at the speed of your buyers
            </p>
            
            <div className="cta-buttons">
              <Link to="/waitlist" className="btn btn-primary" onClick={handleTrialClick}>
                <span className="btn-text">Create Account</span>
                <div className="btn-glow"></div>
              </Link>
              <Link to="/contact" className="btn btn-secondary" onClick={handleDemoClick}>
                <span className="btn-text">Book a Demo</span>
                <div className="btn-ripple"></div>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}